'use client'

import { InfoCircleIcon } from '@lastbrand/web-ui/icons'
import { Tooltip, TooltipProvider } from '@lastbrand/web-ui/tooltip'
import { css, cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { FormattedPrice } from '@src/blocks/currency'
import { BrandLogo } from '@src/components/widgets/navigation/brand-logo'
import { RichText } from '@src/quince-components/src/molecules'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'

import type { TransparentPricingDSProps } from './transparent-pricing-ds.types'

const ICON_SIZE = 16

// Match the legacy TransparentPricing comparison: it renders the `Column` atom
// with `smallerUpper={traditionalPrice > quincePrice}` and no `upperHeight`, so
// the cantaloupe "upper" is a FIXED height (not proportional to price) — the
// dearer column gets the full height and the cheaper one a short height. Values
// from column.module.scss desktop (17rem / 4.9rem), which also match the Figma
// desktop frame (6236:44538 → 169 / 48).
const COMPARISON_BAR_TALL_PX = 170
const COMPARISON_BAR_SHORT_PX = 49
// Black "TOTAL COST" base segment height.
const BASE_SEGMENT_HEIGHT_PX = 96

const getCantaloupeHeight = (isSmaller: boolean): number =>
  isSmaller ? COMPARISON_BAR_SHORT_PX : COMPARISON_BAR_TALL_PX

const rootCss = css.raw({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  w: 'full',
  px: { base: 'space-16', lg: 'space-48' },
})

const contentCss = css({
  display: 'flex',
  flexDirection: { base: 'column', lg: 'row' },
  alignItems: { base: 'stretch', lg: 'flex-start' },
  gap: { base: 'space-40', lg: 'space-120' },
  py: { base: 'space-64', lg: 'space-80' },
  w: 'full',
  maxW: { lg: '1504px' },
  color: 'black',
})

const textCss = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space-24',
  flex: { lg: '1 0 0' },
  maxW: { lg: '500px' },
})

const headerCss = css({
  textStyle: { base: 'title-28-dec', lg: 'title-40-dec' },
  overflowWrap: 'break-word',
})

const bodyCss = css({ textStyle: 'body-16', overflowWrap: 'break-word' })

const listCss = css({
  display: 'flex',
  flexDirection: 'column',
  flex: { lg: '1 0 0' },
  maxW: { lg: '400px' },
  w: 'full',
})

const entryCss = css({ display: 'flex', flexDirection: 'column', w: 'full' })

const dividerCss = css.raw({ h: '1px', w: 'full' })
const dividerDefaultCss = css(dividerCss, { bg: 'darkgray-trans-20' })
const dividerStrongCss = css(dividerCss, { bg: 'black' })

const rowCss = css.raw({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'space-16',
  py: 'space-16',
  w: 'full',
  color: 'black',
})
const rowDefaultCss = css(rowCss)
const rowTotalCss = css(rowCss)

const labelDefaultCss = css({ textStyle: 'title-12', flex: '1 0 0', minW: '0', overflowWrap: 'break-word' })
const labelTotalCss = css({ textStyle: 'title-18', flex: '1 0 0', minW: '0', overflowWrap: 'break-word' })
const priceDefaultCss = css({ textStyle: 'body-12', textAlign: 'right', whiteSpace: 'nowrap' })
const priceTotalCss = css({ textStyle: 'body-18', textAlign: 'right', whiteSpace: 'nowrap' })

const graphCss = css({
  display: { base: 'none', lg: 'flex' },
  alignItems: 'flex-end',
  gap: 'space-48',
})

const barColumnCss = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  w: '125px',
})

const barPriceWrapCss = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: 'space-8',
  py: 'space-16',
  w: 'full',
})

const barPriceCss = css({ textStyle: 'title-24-dec', textAlign: 'center', color: 'black' })

const barInfoCss = css({
  position: 'absolute',
  top: '0',
  right: '0',
  display: 'flex',
  alignItems: 'center',
  p: 'space-4',
})

// Unstyled button so the disclaimer tooltip trigger is keyboard-focusable
// (web-ui Tooltip uses Radix Trigger `asChild`, which doesn't make a bare SVG focusable).
const barInfoButtonCss = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: 0,
  border: 'none',
  bg: 'transparent',
  cursor: 'pointer',
  color: 'inherit',
})

const cantaloupeSegmentCss = css({ bg: 'cantaloupe-100', w: 'full' })

const blackSegmentCss = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bg: 'black',
  px: 'space-8',
  w: 'full',
  h: `${BASE_SEGMENT_HEIGHT_PX}px`,
})

const blackSegmentLabelCss = css({ textStyle: 'title-12', color: 'white', textAlign: 'center' })

const sourceCss = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  px: 'space-8',
  py: 'space-12',
  h: '56px',
  w: 'full',
})

const sourceTextCss = css({ textStyle: 'title-12', color: 'black', textAlign: 'center' })

// Figma "Logo" node (6236:46747) is 53×16, black wordmark on paperwhite.
const brandLogoCss = css({ w: '53px', h: '16px', fill: 'black' })

const renderEntry = (label: React.ReactNode, price: React.ReactNode, isTotal: boolean, key: React.Key) => (
  <div className={entryCss} key={key}>
    <div className={isTotal ? dividerStrongCss : dividerDefaultCss} />
    <div className={isTotal ? rowTotalCss : rowDefaultCss}>
      <span className={isTotal ? labelTotalCss : labelDefaultCss}>{label}</span>
      <span className={isTotal ? priceTotalCss : priceDefaultCss}>{price}</span>
    </div>
    {isTotal ? <div className={dividerStrongCss} /> : null}
  </div>
)

export const TransparentPricingDS = ({ items = [], ...props }: TransparentPricingDSProps) => {
  const { t } = useTranslation('common')
  const { isB2BMode } = useStorefrontModeContext()

  const totalPrice = items.reduce((total, curr) => total + curr.value, 0)
  const { quincePrice, traditionalPrice } = props
  const showComparison = !!traditionalPrice

  // Legacy `.container_b2bMode` used a pure-white background in B2B mode; keep that parity.
  // eslint-disable-next-line i18next/no-literal-string -- Panda color tokens, not user-facing copy
  const bgColor = isB2BMode ? 'white' : 'paperwhite'

  const renderBarColumn = (price: number, source: React.ReactNode, isSmaller: boolean, tooltipText?: string) => (
    <div className={barColumnCss}>
      <div className={barPriceWrapCss}>
        <span className={barPriceCss}>
          <FormattedPrice amount={price} />
        </span>
        {tooltipText ? (
          <span className={barInfoCss}>
            <TooltipProvider>
              <Tooltip textLabel={tooltipText} aria-label={tooltipText} position="top">
                <button type="button" aria-label={tooltipText} className={barInfoButtonCss}>
                  <InfoCircleIcon width={ICON_SIZE} height={ICON_SIZE} data-testid="transparent-pricing-info-icon" />
                </button>
              </Tooltip>
            </TooltipProvider>
          </span>
        ) : null}
      </div>
      <div className={cantaloupeSegmentCss} style={{ height: getCantaloupeHeight(isSmaller) }} />
      <div className={blackSegmentCss}>
        <span className={blackSegmentLabelCss}>{t('total_cost')}</span>
      </div>
      <div className={sourceCss}>{source}</div>
    </div>
  )

  return (
    <div className={cx(css(rootCss, { bg: bgColor }), props.className)}>
      <div className={contentCss}>
        <div className={textCss}>
          <div className={headerCss}>
            {props.headerRichTextProps ? (
              <RichText {...props.headerRichTextProps} />
            ) : (
              (props.header ?? t('transparent_pricing'))
            )}
          </div>
          <div className={bodyCss}>
            {props.bodyRichTextProps ? (
              <RichText {...props.bodyRichTextProps} />
            ) : (
              (props.body ?? t('transparent_pricing_body'))
            )}
          </div>
        </div>

        <div className={listCss}>
          {items.map((item, key) => renderEntry(item.name, <FormattedPrice amount={item.value} />, false, key))}
          {items.length > 0
            ? // eslint-disable-next-line i18next/no-literal-string -- React list key, not user-facing copy
              renderEntry(t('total_cost'), <FormattedPrice amount={totalPrice} />, true, 'total')
            : null}
        </div>

        {showComparison ? (
          <div className={graphCss}>
            {renderBarColumn(
              quincePrice,
              <BrandLogo className={brandLogoCss} srOnlyLabel={t('quince')} />,
              traditionalPrice > quincePrice,
            )}
            {renderBarColumn(
              traditionalPrice,
              <span className={sourceTextCss}>{t('traditional_retailer')}</span>,
              false,
              t('traditional_retailer_disclaimer'),
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default TransparentPricingDS
